import React, { Component } from 'react';
import { bindActionCreators, Dispatch, AnyAction } from 'redux';
import { connect } from 'react-redux';
import { Dropdown, Link, Icon } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import styles from './Header.css';
import { redirectWithClientId, switchField } from '../../utils/url';
import { BASE_FIELDS_URL_NEW_ARCH } from '../../constants/urls';
import {
  currentFieldSelector,
  accessibleFieldsSelector,
  currentOperationsSelector,
  fieldSearchIndexSelector,
} from '../../selectors/fieldsSelectors';
import { openModal } from '../../actions/modalActions';
import { clearFieldNameSearch } from '../../actions/fieldsActions';
import { fetchFieldNameSearch } from '../../thunks/uapiFieldsThunks';
import { OPERATION_NOT_ALLOWED } from '../../constants/modal';
import { FieldState } from '../../actions/field/types/state';
import { AppState } from '../../store/types/Store';
import { ModalMode, ModalSize } from '../../types/Modal';
import type { FieldSearchDescriptor } from '../../requesters/uapiFieldsRequester/uapiFieldsRequester';
import type { OperationCurrent } from '../../utils/operationUtils';
import type { FieldsState } from '../../reducers/fieldsReducer';

interface AccessibleField {
  id: string;
  name: string;
}

interface OwnProps {
  index: number;
}

interface StateProps {
  field: FieldState;
  fields: FieldSearchDescriptor[] | AccessibleField[];
  operation: string | null;
  operations: string[];
  isProxying: boolean;
}

interface DispatchProps {
  openModal: typeof openModal;
  searchFieldsByName: (searchTerm: string) => void;
  clearFieldNameSearch: typeof clearFieldNameSearch;
}

type HeaderProps = OwnProps & StateProps & DispatchProps;

const onSelectField = (_fieldName: string, id: string) => {
  switchField(id);
};

type ExtendedAppState = AppState & {
  operations: { current: OperationCurrent; allIds: string[] };
  fields: FieldsState;
  user: { isProxying: boolean };
};

export class Header extends Component<HeaderProps> {
  onClickEditField = () => {
    const { operation, openModal: openModalProp, field, index, operations } = this.props;
    const baseFieldsUrl = BASE_FIELDS_URL_NEW_ARCH;
    if (operation === 'all-operations' || operations.length > 1) {
      openModalProp(index, null as never, {
        type: OPERATION_NOT_ALLOWED,
        size: ModalSize.M,
        mode: ModalMode.NONE,
      });
    } else {
      redirectWithClientId(baseFieldsUrl, {
        'field-id': field.id,
        view: 'editField',
      });
    }
  };

  onSearchFilter = (searchTerm: string) => {
    const { isProxying } = this.props;
    if (isProxying) return;

    if (searchTerm && searchTerm.trim().length > 0) {
      this.props.searchFieldsByName(searchTerm);
    } else {
      this.props.clearFieldNameSearch();
    }
  };

  render() {
    const { field, fields, isProxying } = this.props;
    const options = fields.map((opField) => ({
      value: opField.name,
      id: String(opField.id),
    }));
    return (
      <header className={styles.header} data-test-id="panel-header">
        <Dropdown
          options={options}
          placeholder={field.name}
          searchPlaceholder={getMessage('searchFieldPlaceholder')}
          searchable
          selectedId={field.id}
          setValue={onSelectField}
          value={field.name}
          {...(isProxying ? {} : { onSearchFilter: this.onSearchFilter })}
        />
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          className={styles.link}
          onClick={this.onClickEditField}
          title={getMessage('editField')}
          data-test-id='edit-field'
        >
          <Icon glyph={Icon.set.EDIT_SHAPE} />
        </Link>
      </header>
    );
  }
}

export function mapStateToProps(state: ExtendedAppState): StateProps {
  const isProxying = state.user?.isProxying ?? false;
  return {
    field: currentFieldSelector(state),
    fields: isProxying ? accessibleFieldsSelector(state) : fieldSearchIndexSelector(state),
    operation: state.operations.current as string | null,
    operations: currentOperationsSelector(state),
    isProxying,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    ...bindActionCreators({ openModal, clearFieldNameSearch }, dispatch),
    searchFieldsByName: (searchTerm: string) =>
      (dispatch as (thunk: ReturnType<typeof fetchFieldNameSearch>) => void)(fetchFieldNameSearch(searchTerm)),
  } as unknown as DispatchProps;
}

export default connect<StateProps, DispatchProps, OwnProps, ExtendedAppState>(mapStateToProps, mapDispatchToProps)(Header);
